import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Typography, 
  TextField, 
  Button, 
  Box,
  Card,
  CardContent,
  SelectChangeEvent
} from '@mui/material';
import { Lightbulb as LightbulbIcon } from '@mui/icons-material';
import apiService, { Platform } from '../services/api';
import OrganizationQuotaBanner from '../components/common/OrganizationQuotaBanner';
import PlatformSelector from '../components/common/PlatformSelector';
import AccountSelector from '../components/common/AccountSelector';
import ICPSelector from '../components/common/ICPSelector';
import FormAlert from '../components/common/FormAlert';
import ExpirationModals from '../components/common/ExpirationModals';
import ProgressModal, { ProgressStep } from '../components/common/ProgressModal';
import { useAuth } from '../contexts/AuthContext';
import useTrialExpirationHandler from '../hooks/useTrialExpirationHandler';
import { useSubscriptionExpirationHandler } from '../hooks/useSubscriptionExpirationHandler';
import useOrganizationInfo from '../hooks/useOrganizationInfo';
import useAccounts, { Account } from '../hooks/useAccounts';
import { logger } from '../utils/logger';

// Define form data interface
interface IdeaFormData {
  accountId: string;
  sector: string;
  platform: string;
  topic: string;
  icpId: string;
}

// Define form field validation rules
const validateForm = (formData: IdeaFormData) => {
  const errors: Record<string, string> = {};
  
  if (!formData.accountId) {
    errors.accountId = 'Account selection is required';
  }
  
  if (!formData.platform) {
    errors.platform = 'Platform selection is required';
  }
  
  if (!formData.topic.trim()) {
    errors.topic = 'Topic is required';
  }
  
  if (!formData.icpId) {
    errors.icpId = 'Audience Persona selection is required';
  }
  
  return errors;
};

const CreateIdea: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearLandingFormData } = useAuth();
  const hasClearedLandingDataRef = useRef(false);
  
  // Check for landing form data passed via location state or sessionStorage fallback
  let landingFormData = location.state?.fromLanding ? location.state.formData : null;

  // If no data in location.state, check sessionStorage as fallback
  if (!landingFormData) {
    const storedNavigation = sessionStorage.getItem('landing_form_navigation');
    if (storedNavigation) {
      try {
        const parsed = JSON.parse(storedNavigation);
        if (parsed.fromLanding && parsed.formData) {
          landingFormData = parsed.formData;
          logger.info('Retrieved landing form data from sessionStorage:', landingFormData);
          // Clear sessionStorage after retrieving to prevent stale data
          sessionStorage.removeItem('landing_form_navigation');
        }
      } catch (error) {
        logger.error('Error parsing sessionStorage navigation data:', error);
      }
    }
  } else {
    logger.info('Landing form data found in location.state:', landingFormData);
  }

  useEffect(() => {
    if (!hasClearedLandingDataRef.current && landingFormData) {
      clearLandingFormData();
      hasClearedLandingDataRef.current = true;
    }
  }, [landingFormData, clearLandingFormData]);
  
  // Extract sector name if it's an object (can happen after express onboarding)
  const landingSectorValue = landingFormData?.sector
    ? (typeof landingFormData.sector === 'object' && landingFormData.sector !== null
        ? (landingFormData.sector as any).name
        : landingFormData.sector)
    : '';
  
  const [formData, setFormData] = useState<IdeaFormData>({
    accountId: '',
    sector: landingSectorValue,
    platform: landingFormData?.platform || 'Twitter/X', // Use landing data or default
    topic: landingFormData?.topic || '',
    icpId: landingFormData?.icpId || ''
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [alertMessage, setAlertMessage] = useState<{type: 'success' | 'error' | 'warning' | 'info', message: string} | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Progress tracking state
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([
    { id: 'user_data', label: 'Preparing User Data', completed: false },
    { id: 'platform_params', label: 'Getting Platform Parameters', completed: false },
    { id: 'idea_generation', label: 'Generating Content Ideas', completed: false },
    { id: 'analysis', label: 'Analyzing Ideas', completed: false },
    { id: 'completion', label: 'Finalizing Results', completed: false }
  ]);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  
  // Use custom hooks
  const { orgInfo } = useOrganizationInfo();
  const { accounts } = useAccounts();
  
  // Trial expiration handling
  const { 
    isExpiredModalOpen: isTrialExpiredModalOpen, 
    isAdmin: isTrialAdmin, 
    handleApiError: handleTrialApiError, 
    closeExpiredModal: closeTrialExpiredModal 
  } = useTrialExpirationHandler();
  
  // Subscription expiration handling
  const { 
    isExpiredModalOpen: isSubscriptionExpiredModalOpen, 
    isAdmin: isSubscriptionAdmin, 
    handleApiError: handleSubscriptionApiError, 
    closeExpiredModal: closeSubscriptionExpiredModal 
  } = useSubscriptionExpirationHandler();

  // Load platforms on component mount
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const platformsData = await apiService.getPlatforms();
        setPlatforms(platformsData);
      } catch (error) {
        if (!handleTrialApiError(error)) {
          logger.error('Error fetching platforms:', error);
          setAlertMessage({
            type: 'error',
            message: 'Failed to load platforms. Please refresh the page.'
          });
        }
      }
    };
    
    fetchPlatforms();
  }, [handleTrialApiError]);

  // Auto-select account if we have landing form data with a brand name
  useEffect(() => {
    if (landingFormData?.brand && accounts.length > 0) {
      // Try to find an account that matches the brand name
      const matchingAccount = accounts.find(
        account => account.name.toLowerCase() === landingFormData.brand.toLowerCase()
      );
      
      if (matchingAccount) {
        // Extract sector name if it's an object
        const sectorValue = typeof matchingAccount.sector === 'object' && matchingAccount.sector !== null
          ? (matchingAccount.sector as any).name
          : matchingAccount.sector || '';
        
        setFormData(prev => ({
          ...prev,
          accountId: matchingAccount.id,
          sector: sectorValue || prev.sector
        }));
      } else if (accounts.length === 1) {
        // If no match but only one account exists, select it
        // Extract sector name if it's an object
        const sectorValue = typeof accounts[0].sector === 'object' && accounts[0].sector !== null
          ? (accounts[0].sector as any).name
          : accounts[0].sector || '';
        
        setFormData(prev => ({
          ...prev,
          accountId: accounts[0].id,
          sector: sectorValue || prev.sector
        }));
      }
    }
  }, [accounts, landingFormData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any previous error for this field
    if (formErrors[name]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    
    // If changing the account, update the sector automatically
    if (name === 'accountId' && value) {
      const selectedAccount = accounts.find(acc => acc.id === value);
      if (selectedAccount) {
        // Handle sector which can be either a string or an object
        const sectorValue = typeof selectedAccount.sector === 'object' && selectedAccount.sector !== null
          ? (selectedAccount.sector as any).name 
          : selectedAccount.sector || '';
        
        setFormData(prev => ({
          ...prev,
          accountId: value,
          sector: sectorValue
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear any previous error for this field
    if (formErrors[name]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Prevent submission if organization has no resources left (ideas use same quota as simulations for now)
    if (orgInfo && orgInfo.plan !== 'INTERNAL' && ((orgInfo.monthlySimQuota || 0) - (orgInfo.simUsed || 0) <= 0)) {
      setAlertMessage({
        type: 'error',
        message: 'Your organization has reached the resource limit. Please contact your administrator to upgrade your plan.'
      });
      return;
    }
    
    // Async save-first flow: no blocking modal
    
    try {
      // Find the selected account to get its name
      const selectedAccount = accounts.find(acc => acc.id === formData.accountId);
      
      if (!selectedAccount) {
        throw new Error('Selected account not found');
      }
      
      // Save-first flow: initialize idea and navigate immediately
      const initRes = await apiService.initIdea({
        brand: selectedAccount.name,
        platform: formData.platform,
        icpId: formData.icpId,
        topic: formData.topic,
        sector: formData.sector,
        count: 5,
        accountId: formData.accountId
      });
      logger.info('Idea initialized successfully:', initRes);
      // Briefly wait until record is visible in prod (handles replication lag)
      try {
        setLoading(true);
        await apiService.waitForIdea(initRes.ideaId, { timeoutMs: 4000, intervalMs: 250 });
      } catch {}
      navigate(`/ideas/${initRes.ideaId}/results`);
      
    } catch (error: any) {
      // Check both trial and subscription expiration first
      if (!handleTrialApiError(error) && !handleSubscriptionApiError(error)) {
        setAlertMessage({
          type: 'error',
          message: error.response?.data?.message || error.message || 'An error occurred while generating ideas'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertMessage(null);
  };

  // Helper function to handle progress updates
  const handleProgressUpdate = (stepId: string, isCompleted: boolean) => {
    if (isCompleted) {
      setProgressSteps(prev => 
        prev.map(step =>
          step.id === stepId 
            ? { ...step, completed: true }
            : step
        )
      );
    } else {
      setCurrentStep(stepId);
    }
  };

  // Reset progress when starting new generation
  const resetProgress = () => {
    setProgressSteps(prev => prev.map(step => ({ ...step, completed: false })));
    setCurrentStep(null);
  };

  return (
    <>
      <Box sx={{ mb: 4, maxWidth: 'md', mx: 'auto' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Generate Ideas
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create engaging content ideas tailored to your brand and audience
        </Typography>
        
        <OrganizationQuotaBanner 
          orgInfo={orgInfo}
          isAdmin={user?.role === 'ADMIN'}
          resourceType="ideas"
        />
      </Box>
      
      <Box sx={{ maxWidth: 'md', mx: 'auto' }}>
        <form onSubmit={handleSubmit}>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Idea Parameters
              </Typography>
              
              <AccountSelector
                accounts={accounts}
                value={formData.accountId}
                onChange={handleSelectChange}
                error={formErrors.accountId}
              />
              
              <TextField
                fullWidth
                margin="normal"
                id="sector"
                name="sector"
                label="Sector"
                value={formData.sector}
                onChange={handleInputChange}
                disabled={true}
                helperText="Automatically updated based on selected account"
              />
              
              <PlatformSelector
                platforms={platforms}
                value={formData.platform}
                onChange={handleSelectChange}
                error={formErrors.platform}
              />
              
              <TextField
                fullWidth
                margin="normal"
                id="topic"
                name="topic"
                label="Topic *"
                multiline
                rows={3}
                value={formData.topic}
                onChange={handleInputChange}
                error={!!formErrors.topic}
                helperText={formErrors.topic || 'Describe the topic or theme for your content ideas'}
                placeholder="e.g., Summer product launch, Customer success stories, Industry trends..."
              />
              
              <ICPSelector
                value={formData.icpId}
                onChange={handleSelectChange}
                error={formErrors.icpId}
              />
            </CardContent>
          </Card>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
            <Button 
              variant="contained" 
              color="primary" 
              size="large" 
              type="submit"
              startIcon={<LightbulbIcon />}
              disabled={loading || Boolean(orgInfo && orgInfo.plan !== 'INTERNAL' && ((orgInfo.monthlySimQuota || 0) - (orgInfo.simUsed || 0) <= 0))}
            >
              {loading ? 'Generating...' : 'Generate Ideas'}
            </Button>
          </Box>
        </form>
        
        {/* Progress Modal */}
        {/* ProgressModal disabled for async flow */}
        <ProgressModal
          open={false}
          title=""
          subtitle=""
          steps={[]}
          currentStep={null}
          type="ideaGen"
        />
        
        {/* Alert for error messages */}
        <FormAlert 
          alertMessage={alertMessage}
          onClose={handleCloseAlert}
        />
        
        {/* Expiration Modals */}
        <ExpirationModals
          isTrialExpiredModalOpen={isTrialExpiredModalOpen}
          isTrialAdmin={isTrialAdmin}
          closeTrialExpiredModal={closeTrialExpiredModal}
          isSubscriptionExpiredModalOpen={isSubscriptionExpiredModalOpen}
          isSubscriptionAdmin={isSubscriptionAdmin}
          closeSubscriptionExpiredModal={closeSubscriptionExpiredModal}
        />
      </Box>
    </>
  );
};

export default CreateIdea; 
